/* eslint-disable @typescript-eslint/ban-types */
import { InputAdornment, MenuItem, Select, Typography } from '@material-ui/core'
import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { SingleFieldProps } from '../../../../../../Types'
import FormToggle from '../../../../../../../Global/Toggle'
import FormTextField from '../../../../../../../Global/TextField'
import { formateBooleanValue } from '../GuarantoType/Utils'
import { useStyles } from './Style'
import { getValue } from './Utils'

type GuarantorState = 'none' | 'optional' | 'required'

const QualificationProfessionalSituation = <T extends {}>({
  field,
  input,
}: SingleFieldProps<T>): React.ReactElement => {
  const classes = useStyles()
  const [allowed, setAllowed] = useState(false)
  const [guarantor, setGuarantor] = useState<GuarantorState>('none')
  const guarantorNeeded = guarantor === 'required'
  const guarantorAsked = guarantor === 'optional'

  /**
   * @description event when input change
   * @param val
   */
  const onChange = (val: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const inputValue = getValue(input)
    input.onChange({
      active: inputValue.active,
      duration: val.target.value,
      guarantor_needed: guarantorNeeded,
      guarantor_asked: guarantorAsked,
    })
  }

  const onAllowedChange = (value: string | boolean) => {
    const isFalse = value === false || value === 'false'
    const inputValue = getValue(input)
    setAllowed(formateBooleanValue(value))
    if (isFalse) {
      setGuarantor('none')
    }
    input.onChange({
      active: formateBooleanValue(value),
      duration: inputValue.duration,
      guarantor_needed: isFalse ? false : guarantorNeeded,
      guarantor_asked: isFalse ? false : guarantorAsked,
    })
  }

  const onGuarantorSelectChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as GuarantorState
    let needed = false
    let asked = false
    if (value === 'none') {
      needed = false
      asked = false
    } else if (value === 'optional') {
      needed = false
      asked = true
    } else if (value === 'required') {
      needed = true
      asked = false
    }
    setGuarantor(value)
    const inputValue = getValue(input)
    input.onChange({
      active: inputValue.active,
      duration: inputValue.duration,
      guarantor_needed: needed,
      guarantor_asked: asked,
    })
  }

  useEffect(() => {
    if (field.customProperties) {
      setAllowed(formateBooleanValue(field.customProperties?.allowed))

      if (field.customProperties?.guarantor_needed) {
        setGuarantor('required')
      } else if (field.customProperties?.guarantor_asked) {
        setGuarantor('optional')
      } else {
        setGuarantor('none')
      }

      input.onChange({
        active: formateBooleanValue(field.customProperties?.allowed),
        duration: field.customProperties?.duration,
        guarantor_needed: !!field.customProperties?.guarantor_needed,
        guarantor_asked: !!field.customProperties?.guarantor_asked,
      })
    }
  }, [field.customProperties])

  return (
    <div className={classes.container}>
      <div className={classes.labelContainer}>
        {field.customProperties?.label && field.customProperties?.label !== '' && (
          <Typography className={classes.label}>{field.customProperties?.label}</Typography>
        )}
        {field.customProperties?.subLabel && field.customProperties?.subLabel('') !== '' && (
          <Typography className={classes.sublabel}>
            {field.customProperties?.subLabel(`${getValue(input).duration}`)}
          </Typography>
        )}
      </div>
      {field.customProperties?.duration || field.customProperties?.duration === 0 ? (
        <div className={classes.durationContainer}>
          <FormTextField
            label={field.customProperties?.durationLabel || ''}
            {...input}
            value={getValue(input).duration}
            onChange={onChange}
            type="number"
            className={classes.durationField}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: <InputAdornment position="end">mois</InputAdornment>,
            }}
          />
        </div>
      ) : null}
      <div className={classes.toggleContainer}>
        <FormToggle
          label={allowed ? 'Situation acceptée' : 'Situation refusée'}
          value={formateBooleanValue(field.customProperties?.allowed) || allowed || false}
          onChange={(value: string | boolean) => onAllowedChange(value)}
        />
        <Select
          value={guarantor}
          onChange={onGuarantorSelectChange}
          disabled={!allowed}
          className={clsx(classes.selectField, !allowed && classes.toggleDisable)}
        >
          <MenuItem value="none">Aucun garant</MenuItem>
          <MenuItem value="optional">Garant optionnel</MenuItem>
          <MenuItem value="required">Garant requis</MenuItem>
        </Select>
      </div>
    </div>
  )
}

export default QualificationProfessionalSituation
